import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface HomePageProps {
  onStartOptimization: () => void;
}

export const HomePage: React.FC<HomePageProps> = ({ onStartOptimization }) => {
  const [expandedStep, setExpandedStep] = useState(0);
  const navigate = useNavigate();
  const steps = [
    {
      title: 'Add Boxes',
      img: '/images/blog/inventory.gif',
      alt: 'Add Boxes',
      desc: (
        <>
          Enter box dimensions and quantities, or upload a CSV file for <strong>Amazon FBA</strong> or <strong>wholesale</strong> shipments. <br />
          <span className="block mt-2">Easily input multiple product types, sizes, and quantities to streamline your palletization process. Our tool validates your data and supports bulk uploads, making it perfect for e-commerce, private label, and supply chain professionals looking to optimize their fulfillment center shipments from the start.</span>
        </>
      ),
    },
    {
      title: 'Optimize',
      img: '/images/blog/pallete.gif',
      alt: 'Optimize',
      desc: (
        <>
          Run the advanced palletization algorithm to find the best arrangement for <strong>fulfillment centers</strong> and <strong>private label</strong> logistics. <br />
          <span className="block mt-2">Our smart algorithm maximizes pallet space, reduces shipping costs, and ensures compliance with Amazon FBA and global logistics standards. Whether you’re shipping to Amazon, a 3PL, or your own warehouse, you’ll get the most efficient stacking and packing for every load.</span>
        </>
      ),
    },
    {
      title: 'Visualize',
      img: '/images/blog/fulfillment.gif',
      alt: 'Visualize',
      desc: (
        <>
          View a 2D visualization of your optimized pallets for <strong>carton optimization</strong> and <strong>shipping</strong> planning. <br />
          <span className="block mt-2">See exactly how your boxes are arranged on each pallet layer, identify unused space, and make adjustments before shipping. Our interactive visualization helps you communicate with your warehouse team, improve packing efficiency, and ensure every shipment is ready for fulfillment center requirements.</span>
        </>
      ),
    },
    {
      title: 'Export',
      img: '/images/blog/stock.gif',
      alt: 'Export',
      desc: (
        <>
          Download your results for <strong>shipping</strong>, <strong>logistics</strong>, and <strong>supply chain</strong> documentation. <br />
          <span className="block mt-2">Export detailed pallet layouts, box arrangements, and compliance reports as CSV or PDF files. Share your results with logistics partners, keep records for audits, and streamline your supply chain management with professional, ready-to-use documentation.</span>
        </>
      ),
    },
  ];

  // Scroll-activated expand logic
  const stepRefs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      const offsets = stepRefs.current.map(ref => {
        if (!ref) return Infinity;
        const rect = ref.getBoundingClientRect();
        // Distance from center of viewport
        return Math.abs(rect.top + rect.height / 2 - window.innerHeight / 2);
      });
      const minIdx = offsets.indexOf(Math.min(...offsets));
      setExpandedStep(minIdx);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    // Initial call
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-purple-800 text-white overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat blur-sm"
          style={{
            backgroundImage: "url('/images/blog/warehouse-scene-with-forklift-stacked-boxes.webp')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/50 via-blue-700/50 to-purple-800/50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">
              Pallet Builder
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-6 sm:mb-8 max-w-3xl mx-auto opacity-90 px-4">
              Advanced FCL optimization tool for efficient pallet stacking and shipping logistics
            </p>
            <button
              onClick={() => navigate('/optimization')}
              className="bg-white text-blue-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Start Optimization →
            </button>
          </div>
        </div>
        
        {/* Hero Image */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-900 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 sm:pb-12">
          <div className="bg-white rounded-xl shadow-2xl p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Smart Optimization</h3>
                <p className="text-gray-600 text-sm">Advanced algorithms for optimal pallet stacking</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Visual Analytics</h3>
                <p className="text-gray-600 text-sm">2D visualization of your optimized pallets</p>
              </div>
              <div className="text-center sm:col-span-2 lg:col-span-1">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">FCL Ready</h3>
                <p className="text-gray-600 text-sm">Designed for Full Container Load shipments</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Powerful Features
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Everything you need for efficient pallet optimization and shipping logistics
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Feature 1 */}
            <div className="bg-gray-50 rounded-xl p-6 sm:p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">CSV Import</h3>
              <p className="text-gray-600 mb-4">
                Upload CSV files with product ASIN, dimensions, and quantities for bulk processing.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Supports multiple product types</li>
                <li>• Automatic data validation</li>
                <li>• Sample templates included</li>
              </ul>
            </div>

            {/* Feature 2 */}
            <div className="bg-gray-50 rounded-xl p-6 sm:p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Product ID Tracking</h3>
              <p className="text-gray-600 mb-4">
                Track product IDs throughout the optimization process for better inventory management.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Product identification</li>
                <li>• Pallet-level tracking</li>
                <li>• Export capabilities</li>
              </ul>
            </div>

            {/* Feature 3 */}
            <div className="bg-gray-50 rounded-xl p-6 sm:p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 sm:mb-6">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Visual Analytics</h3>
              <p className="text-gray-600 mb-4">
                View detailed 2D visualizations of your optimized pallets for better planning.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Interactive 2D views</li>
                <li>• Layer-by-layer analysis</li>
                <li>• Export capabilities</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How to Optimize Your Amazon FBA Palletization
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Simple steps to maximize your shipping efficiency for <strong>Amazon FBA</strong>, <strong>fulfillment centers</strong>, <strong>private label</strong>, and <strong>wholesale</strong> shipments.<br />
              Streamline your supply chain and reduce costs with our advanced pallet optimization tool.
            </p>
          </div>

          {/* Animated Timeline */}
          <div className="relative mx-auto max-w-2xl min-h-[600px]">
            <div className="border-l-4 border-blue-200 absolute left-6 top-0 h-full z-0" aria-hidden="true"></div>
            <ul className="space-y-16 sm:space-y-20 relative z-10">
              {steps.map((step, idx) => (
                <li
                  key={step.title}
                  className="flex items-start group"
                  ref={el => { stepRefs.current[idx] = el; }}
                >
                  {/* Timeline Dot */}
                  <div className="flex flex-col items-center mr-4 sm:mr-8">
                    <span className={`w-6 h-6 rounded-full border-4 ${expandedStep === idx ? 'border-blue-600 bg-blue-600' : 'border-blue-200 bg-white'} transition-all duration-300`}></span>
                    {idx < steps.length - 1 && <span className="flex-1 w-px bg-blue-200"></span>}
                  </div>
                  {/* Step Content */}
                  <div className={`flex-1 bg-white rounded-xl p-4 sm:p-6 shadow transition-all duration-300 ${expandedStep === idx ? 'scale-105 border border-blue-100' : 'opacity-70'}`}
                    style={{ minWidth: 0 }}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                      <img src={step.img} alt={step.alt} className={`object-contain mx-auto sm:mx-0 ${expandedStep === idx ? 'w-24 h-24 sm:w-32 sm:h-32' : 'w-16 h-16 sm:w-20 sm:h-20'} transition-all duration-300`} />
                      <div className="flex-1 text-center sm:text-left">
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{step.title}</h3>
                        <div
                          className={`transition-all duration-500 ease-in-out overflow-hidden ${expandedStep === idx ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}
                          style={{ minHeight: expandedStep === idx ? 80 : 0 }}
                        >
                          <p className="text-gray-700 text-sm sm:text-base animate-fade-in mt-2">{step.desc}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6">
            Ready to Optimize Your Pallets?
          </h2>
          <p className="text-lg sm:text-xl mb-6 sm:mb-8 opacity-90 px-4">
            Start optimizing your FCL shipments today with our advanced pallet builder tool.
          </p>
          <button
            onClick={() => navigate('/optimization')}
            className="bg-white text-blue-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Start Optimization Now →
          </button>
        </div>
      </section>
    </div>
  );
}; 